import { useContext, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./Card";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import { KpiCardsSkeleton } from "./skeletons/KpiCardsSkeleton";
import { UserContext } from "../../../../App";
import OrgApi from "../../../../API/OrgApi";
import { ConsoleLogger } from "@microsoft/signalr/dist/esm/Utils";

function KpiCard({ title, color, actual, target, percentage }) {
	console.log({ percentage });
	return (
		<Card className="flex-1">
			<CardContent className="flex flex-col gap-2">
				<div className="text-sm font-medium text-nowrap text-slate-900">
					{title}
				</div>
				<div className="flex items-center justify-between">
					<div className="text-2xl font-bold" style={{ color: color }}>
						{percentage}%
					</div>
					{/* <div
						className={`flex items-center ${
							true ? "text-green-500" : "text-red-500"
						}`}
					>
						{true ? (
							<ArrowUpIcon className="h-4 w-4" />
						) : (
							<ArrowDownIcon className="h-4 w-4" />
						)}
						<span className="ml-1">40%</span>
					</div> */}
				</div>
				<div className="flex items-center gap-3">
					<div className="h-2 flex-1 rounded-full bg-gray-200 overflow-x-hidden">
						<div
							className={`h-2 rounded-full`}
							style={{
								width: `${percentage}%`,
								backgroundColor: color,
							}}
						></div>
					</div>
					<div className="text-sm text-gray-700 font-medium">
						{actual.toLocaleString()}
					</div>
				</div>
				<div className="text-sm text-gray-600 font-medium">
					Goal: {target.toLocaleString()}
				</div>
			</CardContent>
		</Card>
	);
}

const startOfWeek = (date = new Date()) => {
	const d = new Date(date);
	const day = d.getDay();
	const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
	return new Date(d.setDate(diff));
};
const endOfWeek = (date = new Date()) => {
	const start = startOfWeek(date);
	return new Date(
		start.getFullYear(),
		start.getMonth(),
		start.getDate() + 6,
		23,
		59,
		59,
		999,
	);
};
const startOfMonth = (date = new Date()) =>
	new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date = new Date()) =>
	new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
const startOfYear = (date = new Date()) => new Date(date.getFullYear(), 0, 1);
const endOfYear = (date = new Date()) =>
	new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999);

const ranges = [
	{ label: "This Week", value: "THIS_WEEK" },
	{ label: "Last Week", value: "LAST_WEEK" },
	{ label: "This Month", value: "THIS_MONTH" },
	{ label: "Last Month", value: "LAST_MONTH" },
	{ label: "This Year", value: "THIS_YEAR" },
	{ label: "Last Year", value: "LAST_YEAR" },
];

export function KpiCards({ fiberCompanyId }) {
	const [data, setData] = useState({
		contacted: {
			actual: 0,
			target: 0,
		},
		sold: {
			actual: 0,
			target: 0,
		},
		installed: {
			actual: 0,
			target: 0,
		},
		earned: {
			actual: 0,
			target: 0,
		},
		cancelled: {
			actual: 0,
			target: 0,
		},
	});
	const [loading, setLoading] = useState(true);
	const user = useContext(UserContext);
	const [range, setRange] = useState(ranges[0]);

	useEffect(() => {
		fetchData();
	}, [range, fiberCompanyId, user]);

	const fetchData = async () => {
		setLoading(true);
		const startDate =
			range.value === "THIS_WEEK"
				? startOfWeek()
				: range.value === "LAST_WEEK"
				? startOfWeek(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
				: range.value === "THIS_MONTH"
				? startOfMonth()
				: range.value === "LAST_MONTH"
				? startOfMonth(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
				: range.value === "THIS_YEAR"
				? startOfYear()
				: range.value === "LAST_YEAR"
				? startOfYear(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000))
				: undefined;

		const endDate =
			range.value === "THIS_WEEK"
				? endOfWeek()
				: range.value === "LAST_WEEK"
				? endOfWeek(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
				: range.value === "THIS_MONTH"
				? endOfMonth()
				: range.value === "LAST_MONTH"
				? endOfMonth(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
				: range.value === "THIS_YEAR"
				? endOfYear()
				: range.value === "LAST_YEAR"
				? endOfYear(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000))
				: undefined;

		const res = await OrgApi.getDashboardGoals({
			userId: user.id,
			startDate,
			endDate,
			fiberCompanyId,
		});
		if (res?.status?.success) {
			const { data } = res;

			setData(data);
			setLoading(false);
		} else {
			console.error("Error fetching data:", res.message);
		}
	};

	if (loading) return <KpiCardsSkeleton />; // Skeleton is handled by the parent component

	return (
		<div className="flex flex-col gap-6 lg:gap-4">
			<div className="flex flex-row-reverse items-center justify-between">
				{/* <div className="hidden lg:block">180 Leads</div> */}
				<div className="flex gap-3">
					<select
						className="flex-1 max-w-44 border shadow-sm rounded-lg py-2.5 px-3.5 pr-8 text-sm"
						value={range.value}
						onChange={(e) =>
							setRange(ranges.find((r) => r.value === e.target.value))
						}
					>
						{ranges.map((r) => (
							<option key={r.value} value={r.value}>
								{r.label}
							</option>
						))}
					</select>
				</div>
			</div>

			<div className="flex flex-wrap flex-col gap-3 lg:flex-row">
				<KpiCard
					data={data.contacted}
					title="Leads Contacted"
					actual={data.contacted.actual}
					target={data.contacted.target}
					percentage={
						data.contacted.target === 0
							? 0
							: ((data.contacted.actual * 100) / data.contacted.target).toFixed(
									0,
							  )
					}
					color="#00AB52"
				/>
				<KpiCard
					data={data.sold}
					title="Leads Sold"
					actual={data.sold.actual}
					target={data.sold.target}
					percentage={
						data.sold.target === 0
							? 0
							: ((data.sold.actual * 100) / data.sold.target).toFixed(0)
					}
					color="#022780"
				/>
				<KpiCard
					title="Customer Installs"
					actual={data.installed.actual}
					target={data.installed.target}
					percentage={
						data.installed.target === 0
							? 0
							: ((data.installed.actual * 100) / data.installed.target).toFixed(
									0,
							  )
					}
					color="#1998CC"
				/>
				{/* <KpiCard data={data.earned} title="Revenue Earned" color="#FF9900" /> */}
				<KpiCard
					data={data.cancelled}
					title="Cancel Rate"
					actual={`${data.cancelled.actual}%`}
					target={`${data.cancelled.target}%`}
					percentage={data.cancelled.actual}
					color="#FC1C0F"
				/>
			</div>
		</div>
	);
}
